<template>
  <form :class="props?.class" ref="formRef">
    <div v-if="!isAuthenticated">
      <div class="flex size-full flex-col items-center justify-center gap-2.5 text-center">
        <IconPopupIconWarning class="size-8" filled />
        <span class="text-lg font-semibold">{{ t("login_required") }}</span>
        <UiButton class="mt-2" @click="() => login.open()">
          {{ $t("authentication.buttons.login") }}
        </UiButton>
      </div>
    </div>
    <div v-else-if="hasPensionRecommendations">
      <div class="flex size-full flex-col items-center justify-center gap-2.5 text-center">
        <IconSuccessGreen filled />
        <span class="block text-lg font-semibold">{{ t("already_has_recommendations") }}</span>
      </div>
    </div>
    <SharedPayMeFieldsBuilder v-else :field-groups="groups" :grid="!isMobile" />
    <slot
      v-bind="{ isSubmitLoading, isValid: isPayMeFormValid, isError, hasPensionRecommendations }"
      name="footer"
    />
  </form>
</template>

<script setup lang="ts">
import { isDevelopment } from "std-env"
import { useJoinPensionRecommendations } from "~/composables/kyc/pension.fetchers"

import { PayMeFields } from "@finq/app-base/constants/payme.constants"
import { FlowStage } from "@finq/app-base/types/flowStage.namespace"

const user = useUser()
const { locale } = useI18n()
const { isAuthenticated } = useUser()
const login = useLogin()
const props = defineProps<{ class?: ClassValue }>()
const formRef = ref<Nullable<HTMLFormElement>>(null)
const isSubmitLoading = ref(false)
const { isMobile } = useDisplay()
const { mutateAsync: joinPensionRecommendationsMutation } = useJoinPensionRecommendations()
const { updateStageMutation, fetchStageQuery } = usePensionFlowStageManager()
const { currentStage } = storeToRefs(usePensionFlowStageManager())

const emits = defineEmits<{
  (e: "success", token: string): void
  (e: "close"): void
}>()

const { t } = useI18n({ useScope: "local" })

const isPayMeFormValid = computed(() => {
  if (hasPensionRecommendations.value) return true

  return isValid.value
})

async function handleSubmit() {
  if (!isValid.value) return

  isSubmitLoading.value = true
  isError.value = false
  try {
    let token = "test-token"
    let newFlowStage = null

    if (isDevelopment) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      const { currentStage: newStage } = await updateStageMutation.mutateAsync({
        currentStage: currentStage.value!,
      })

      newFlowStage = newStage
    } else {
      const { token: paymeToken, card } = await instance.value?.tokenize({
        payerEmail: user?.user.value?.email,
        total: {
          label: "Pension recommendations",
          amount: { value: "400", currency: "ILS" },
        },
      })!
      token = paymeToken

      const { success } = await joinPensionRecommendationsMutation({
        token,
        cardMask: card.cardMask,
        cardExp: card.expiry,
      })

      if (!success) {
        isError.value = true
      } else {
        const { data } = await fetchStageQuery.refetch()
        newFlowStage = data?.currentStage
      }
    }

    if (newFlowStage === FlowStage.StageKey.RECOMMENDATIONS) {
      console.log("[RecommendationCheckoutForm] emit success")
      emits("success", token)
    } else {
      console.log("[RecommendationCheckoutForm] emit close")
      // TODO)) handle the case where the stage is not recommendations
      emits("close")
    }
  } catch (error) {
    console.error("Error during payment process:", error)
    isError.value = true
  } finally {
    isSubmitLoading.value = false
  }
}

const { instance, groups, isValid, isError } = useInitializePayMeInstance({
  groups: [
    { fields: [PayMeFields.NUMBER] },
    { fields: [PayMeFields.EXPIRATION, PayMeFields.CVC] },
    { fields: [PayMeFields.NAME_FIRST, PayMeFields.NAME_LAST] },
    { fields: [PayMeFields.SOCIAL_ID, PayMeFields.PHONE] },
  ],
  form: {
    language: locale.value,
    ref: formRef,
    onSubmit: handleSubmit,
  },
})

const hasPensionRecommendations = computed(
  () => false
  // user.canAccessProduct(PricingProductEnum.Pensions, [PricingPlanTypes.PlanCode.MANAGED])
)
</script>

<i18n lang="json">
{
  "en": {
    "new_card": "New card",
    "card_that_ends_with": "Card that ends with {lastDigits}",
    "login_required": "You need to login to continue",
    "already_has_recommendations": "You already have active subscription "
  },
  "he": {
    "new_card": "כרטיס חדש",
    "card_that_ends_with": "כרטיס שמסתיים ב{lastDigits}",
    "login_required": "יש להתחבר כדי להמשיך",
    "already_has_recommendations": "יש לך כבר מינוי פעיל"
  }
}
</i18n>
